import { useState, useEffect, useCallback } from 'react'

import { Button, Table } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'

import Badge from '../../components/_atoms/Badge/Badge'
import BadgeStatus from '../../components/_atoms/BadgeStatus/BadgeStatus'
import NumChange from '../../components/_atoms/NumChange/NumChange'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import ColumnSortRTK from '../../components/_molecules/ColumnSort/ColumnSortRTK'
import SearchComponentRTK from '../../components/_molecules/SearchComponent/SearchComponentRTK'
import { ModalBulkPriceOperationDetails } from '../../components/_molecules/ModalBulkPriceOperationDetails/ModalBulkPriceOperationDetails'
import { ModalBulkPriceRevertConfirm } from '../../components/_molecules/ModalBulkPriceRevertConfirm/ModalBulkPriceRevertConfirm'
import { ToastType, useToast } from '../../hooks/useToast'
import { fontSizes, fontWeights } from '../../constants/salesStyles'
import {
    useGetActionLogListQuery,
    useRevertBulkPriceChangeMutation,
} from '../../modules/store/datahubApi/datahubApi'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import {
    useGetDealStatusDictQuery
} from '../../modules/store/dochubApi/dochubApi'
import { ActionLog as ActionLogItem } from '../../types/actionLog/actionLogTypes'
import { SortFieldActionLogs, SortIconType, SortOrder } from '../../types/common/commonTypes'
import { countDaysInStock, formatDate, num_word } from '../../utils/converters'
import { colWidth } from '../../utils/stylePatches'

import styles from './styles.module.scss'
import { DealStatus } from '../../modules/store/dochubApi/dochubTypes'
import ArrowIconButton from '../../components/_atoms/ArrowIconButton/ArrowIconButton'

interface PriceRange {
    from: number
    to: number
}

interface ActionLogFilters {
    page: number
    perPage: number
    filterVendorCode: string
    partName: string
    priceRange: PriceRange | null
    modelId: number | null
    markId: number | null
    partNumber: string
    sortOrder: [string, SortOrder][]
}

const initialFilters: ActionLogFilters = {
    page: 1,
    perPage: 50,
    filterVendorCode: '',
    partName: '',
    priceRange: null,
    modelId: null,
    markId: null,
    partNumber: '',
    sortOrder: [],
}

const getBulkPriceSummary = (actionLog: ActionLogItem) => {
    const { details } = actionLog
    const partsCount = details?.partsCount ?? 0

    if (actionLog.actionType === 'bulk_price_revert') {
        return `Отменено для ${partsCount} запч.`
    }

    if (details?.mode === 'fixed') {
        return `${partsCount} запч. → ${details.fixedPrice?.toLocaleString('ru-RU')} ₽`
    }

    if (details?.mode === 'percent' && details.percentValue !== undefined && details.percentValue !== null) {
        const sign = details.percentValue > 0 ? '+' : ''
        return `${partsCount} запч. ${sign}${details.percentValue}%`
    }

    return `${partsCount} запч.`
}

const ActionLog = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const searchPage = Number(searchParams.get('page')) || 1

    const [filters, setFilters] = useState<ActionLogFilters>({
        ...initialFilters,
        page: searchPage,
    })
    const [detailsOperationId, setDetailsOperationId] = useState<string | null>(null)
    const [revertTarget, setRevertTarget] = useState<ActionLogItem | null>(null)
    const { page, perPage, filterVendorCode, partName, priceRange, modelId, markId, partNumber, sortOrder } = filters
    const priceFrom = priceRange?.from
    const priceTo = priceRange?.to

    const {
        data: dealStatusDictData,
    } = useGetDealStatusDictQuery()

    const openDeal = useCallback(
        (dealId: string) => {
            window.open(getAdminNavigationPath(adminRouteAlias.deals.edit) + `/${dealId}`, '_blank', 'noopener,noreferrer')
        },
        [],
    )

    useEffect(() => {
        const currentParam = searchParams.get('page')
        if (currentParam !== String(page)) {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev)
                newParams.set('page', String(page))
                return newParams
            })
        }
    }, [page, searchParams, setSearchParams])

    const { data, isLoading, isFetching, error, refetch } = useGetActionLogListQuery({
        objectType: 'part',
        pagination: { page, perPage },
        order: sortOrder,
        modelId,
        markId,
        search: partName,
        priceRange: priceRange ? { from: priceFrom, to: priceTo } : undefined,
        partNumber,
        filterVendorCode,
    }, {
        refetchOnMountOrArgChange: true,
    })

    const [revertBulkPriceChange, { isLoading: isRevertLoading, error: revertError, data: revertData }] =
        useRevertBulkPriceChangeMutation()

    const actionLogList = data?.data?.data || []
    const itemsCount = data?.data?.dataCount || 0

    useToast({ isTriggered: !!revertError, type: ToastType.ERROR, message: 'Не удалось отменить изменение цен' })
    useToast({
        isTriggered: !!revertData?.success,
        type: ToastType.SUCCESS,
        message: `Цены возвращены (${revertData?.revertedCount ?? 0} запч.)`,
    })

    useEffect(() => {
        if (revertData?.success) {
            setRevertTarget(null)
            refetch()
        }
    }, [refetch, revertData])

    const confirmRevert = () => {
        if (!revertTarget) {
            return
        }

        revertBulkPriceChange({
            operationId: revertTarget.details?.operationId || revertTarget.objectId,
        })
    }

    if (error) {
        return <div>Ошибка загрузки данных</div>
    }

    const currentSortOrderForDate =
        filters.sortOrder.find(item => item[0] === SortFieldActionLogs.UPDATED_AT)?.[1] || SortOrder.NONE

    return (
        <div className={styles.wrap}>
            {detailsOperationId && (
                <ModalBulkPriceOperationDetails
                    isOpen={!!detailsOperationId}
                    closeModal={() => setDetailsOperationId(null)}
                    operationId={detailsOperationId}
                />
            )}
            {revertTarget && (
                <ModalBulkPriceRevertConfirm
                    isOpen={!!revertTarget}
                    closeModal={() => setRevertTarget(null)}
                    confirmRevert={confirmRevert}
                    partsCount={revertTarget.details?.partsCount ?? 0}
                    operationDate={revertTarget.updatedAt}
                    isLoading={isRevertLoading}
                />
            )}

            <div className={styles.rowWrap}>
                <SalesText
                    text={'Журнал действий'}
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
            </div>

            <div className={styles.searchWrap}>
                <SearchComponentRTK
                    searchTerm={filters.partName}
                    setSearchTerm={(newTerm) =>
                        setFilters(prev => ({ ...prev, partName: newTerm, page: 1 }))
                    }
                    placeholder="Поиск по журналу"
                />
            </div>

            <Table striped hover>
                <thead>
                    <tr>
                        <th scope="col" style={colWidth(100)}>
                            Дата
                            <ColumnSortRTK
                                iconType={SortIconType.NUMERIC}
                                sortField={SortFieldActionLogs.UPDATED_AT}
                                reverseSort={true}
                                currentSortOrder={currentSortOrderForDate}
                                onSortChange={(field, newOrder) => {
                                    setFilters(prev => {
                                        const newSort = prev.sortOrder.filter(item => item[0] !== field)
                                        if (newOrder !== SortOrder.NONE) {
                                            newSort.push([field, newOrder])
                                        }
                                        return { ...prev, sortOrder: newSort, page: 1 }
                                    })
                                }}
                            />
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Аккаунт
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Тип Действия
                        </th>
                        <th scope="col" style={colWidth(250)}>
                            Запчасть
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Марка
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Модель
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Артикул
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            OEM
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Цена
                        </th>
                        <th scope="col" style={colWidth(250)}>
                            Изменения
                        </th>
                    </tr>
                </thead>
                {(isLoading || isFetching) ? (
                    <ListSkeleton rowCount={9} columnCount={10} marginVerticalRem={0.21} />
                ) : (
                    <tbody>
                        {actionLogList.map((actionLog: ActionLogItem, index: number) => {
                            const isBulkPriceAction =
                                actionLog.actionType === 'bulk_price_update' ||
                                actionLog.actionType === 'bulk_price_revert'
                            const operationId = actionLog.details?.operationId || actionLog.objectId
                            const canRevertBulkPrice =
                                actionLog.actionType === 'bulk_price_update' && !actionLog.details?.revertedAt

                            return (
                                <tr key={index} className={styles.row}>
                                    <td scope="row">
                                        <div>
                                            <Badge text={formatDate(actionLog.updatedAt)} color="#ddd" fontColor="#555" />
                                        </div>
                                        {+countDaysInStock(`${actionLog.updatedAt}`) > 10 && (
                                            <div>
                                                <Badge
                                                    text={
                                                        countDaysInStock(`${actionLog.updatedAt}`) +
                                                        ' ' +
                                                        num_word(+countDaysInStock(`${actionLog.updatedAt}`), ['день', 'дня', 'дней'])
                                                    }
                                                    color="#eee"
                                                    fontColor="#777"
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td scope="row">{actionLog.details?.telephoneNumber}</td>
                                    <td scope="row">
                                        <div>
                                            {actionLog.actionTypeText}
                                            {actionLog.details?.deal && (
                                                <ArrowIconButton
                                                    onClick={() => openDeal(actionLog.details.deal!.id)}
                                                    marginLeft={4}
                                                    marginRight={4}
                                                    color="#777"
                                                />)}
                                        </div>
                                    </td>
                                    <td scope="row">{actionLog.objectData?.title}</td>
                                    <td scope="row">{actionLog.objectData?.markTitle}</td>
                                    <td scope="row">{actionLog.objectData?.modelTitle}</td>
                                    <td scope="row">{actionLog.objectData?.vendorCode}</td>
                                    <td scope="row">{actionLog.objectData?.partNumber}</td>
                                    <td scope="row">{isBulkPriceAction ? '—' : actionLog.objectData?.price}</td>
                                    <td scope="row">
                                        {isBulkPriceAction && (
                                            <div className={styles.bulkPriceChange}>
                                                <div>{getBulkPriceSummary(actionLog)}</div>
                                                {actionLog.actionType === 'bulk_price_update' && (
                                                    <Button
                                                        variant="link"
                                                        className="p-0"
                                                        onClick={() => setDetailsOperationId(operationId)}
                                                    >
                                                        Подробнее
                                                    </Button>
                                                )}
                                                {canRevertBulkPrice && (
                                                    <Button
                                                        variant="link"
                                                        className="p-0 ms-2"
                                                        onClick={() => setRevertTarget(actionLog)}
                                                    >
                                                        Отменить изменение цен
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        {actionLog.details?.qty && (
                                            <div>Кол.: <NumChange change={actionLog.details.qty} /></div>
                                        )}
                                        {actionLog.details?.deal && (
                                            <div className={styles.dealChange}>
                                                --
                                                <div>
                                                    <a
                                                        href={getAdminNavigationPath(adminRouteAlias.deals.edit) + `/${actionLog.details.deal.id}`}
                                                        target={'_blank'}
                                                        rel={'noopener noreferrer'}
                                                        style={{
                                                            marginRight: '4px',
                                                        }}
                                                    >Сделка:</a>
                                                    {actionLog.details?.deal?.status && (
                                                        <BadgeStatus
                                                            text={
                                                                dealStatusDictData
                                                                    ? dealStatusDictData.data[actionLog.details?.deal?.status as DealStatus]?.name || '---'
                                                                    : actionLog.details?.deal?.status
                                                            }
                                                            status={actionLog.details?.deal?.status}
                                                        />
                                                    )}
                                                </div>
                                                {actionLog.details?.deal?.price?.after && (
                                                    <div>Цена: <NumChange change={actionLog.details.deal.price} /></div>
                                                )}
                                                {actionLog.details?.deal?.qty?.after && (
                                                    <div>Шт.: <NumChange change={actionLog.details.deal.qty} /></div>
                                                )}
                                            </div>
                                        )}
                                        {actionLog.details?.price && (
                                            <div>Цена: <NumChange change={actionLog.details.price} /></div>
                                        )}
                                        {actionLog.details?.soldPrice && (
                                            <div>Цена продажи: {actionLog.details.soldPrice.toLocaleString('ru-RU')}</div>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                )}
            </Table>
            {(!isLoading && actionLogList.length === 0) ? (
                <ListNoItems slice={'actionLogList'} />
            ) : (
                <ListPagination
                    page={page}
                    setPage={(newPage: number) => setFilters(prev => ({ ...prev, page: newPage }))}
                    perPage={perPage}
                    itemsCount={itemsCount}
                    justifyContent={'flex-end'}
                />
            )}
        </div>
    )
}

export default ActionLog
